import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Form, Input, Row, Spin, Tabs } from 'antd';
import { guid } from 'odata-query';
import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrganizationsSelect from 'src/common/components/business/organization-select';
import UserAutocomplete from 'src/common/components/business/user-autocomplete';
import { CommandResult } from 'src/core/stores/types';
import NewItemModal from 'src/core/ui/newitemmodal';
import { container } from 'src/inversify.config';
import RoleAssignmentAttributesComponent from 'src/pages/roles/role-attributes/role-attributes';
import { CreateGroupItem, GroupItemStore, GroupType } from 'src/stores/groups-store';
import {
  CreateStructmasterLicenseItem,
  CreateUnsignedStructmasterLicense,
  StructmasterLicensesStore,
  UnsignedStructmasterLicense,
} from 'src/stores/licensing-store';
import { OrganizationItemStore, ORGANIZATIONS_MEMBER_LIMIT } from 'src/stores/organizations-store';
import { isEmailOrGuid, nameof } from 'src/core/utils/object';
import { RoleSummaryStore, StructmasterUserRoleName } from 'src/stores/roles-store';
import { RuleObject } from 'antd/es/form';
import { curateString } from 'src/core/utils/object';

type StructmasterLicenseGroupAssignmentProps = {
  onClose: (response?: CommandResult<UnsignedStructmasterLicense>) => void;
  canCreateUnsigned: boolean;
};

const StructmasterLicenseGroupAssignment: FC<StructmasterLicenseGroupAssignmentProps> = ({ onClose, canCreateUnsigned }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [customError, setCustomError] = useState('');

  const newGroupStore = container.get(GroupItemStore);
  const newGroupState = newGroupStore.state;

  const structmasterLicensesStore = container.get(StructmasterLicensesStore);

  const disabledUsersRef = useRef<string[]>([]);
  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [currentOrganizationMembers, setCurrentOrganizationMembers] = useState<string[]>([]);
  const [groupUserCount, setGroupUserCount] = useState<number>(0);
  const [formUsers, setFormUsers] = useState<{ label: string; value: string }[]>([]);
  const rolesStore = container.get(RoleSummaryStore);
  const rolesState = rolesStore.state;
  const [isLoading, setIsLoading] = useState(false);

  const onNewItem = async () => {
    setIsLoading(true);
    let item: any;
    try {
      item = (await form.validateFields()) as any;
    } catch {
      setCustomError(t('CheckLicenseInfoAndUsersTabs'));
      setIsLoading(false);
      return;
    }

    const groupName = curateString(item.name);
    const organizationId = item.organization.value;

    const duplicatedGroups = await structmasterLicensesStore.getStructmasterLicenses({
      query: {
        filter: { LicenseName: groupName, OrganizationId: guid(organizationId) },
      },
    });

    if (duplicatedGroups?.count) {
      setCustomError(t('ThereIsAlreadyAStructmasterLicenseWithThisName'));
      setIsLoading(false);
      return;
    }

    setCustomError('');

    const expirationDate = item.expirationDate;

    await rolesStore.load({ filter: { organizationId: guid(organizationId), name: StructmasterUserRoleName }, take: 1, skip: 0 });
    const roleId = [...rolesState.items.value].firstOrDefault()?.id!;

    const body: CreateStructmasterLicenseItem = {
      createGroupBody: {
        name: curateString(item.name),
        organizationId: item.organization.value,
        members: (item.members || []).map((x: any) => x.user.value),
        groupType: GroupType.Structmaster,
      },
      expirationDate,
      roleId,
    };

    let response = await structmasterLicensesStore.createLicense(body);

    setIsLoading(false);
    if (response?.isSuccess) {
      form.resetFields();
      onClose(response);
    } else {
      setCustomError(t('ErrorCreatingLicense'));
    }
  };

  const onNewItemUnsigned = async () => {
    let item: any;
    try {
      item = (await form.validateFields()) as any;
    } catch {
      setCustomError(t('CheckLicenseInfoAndUsersTabs'));
      return;
    }

    const groupName = curateString(item.name);
    const organizationId = item.organization.value;

    const duplicatedGroups = await structmasterLicensesStore.getStructmasterLicenses({
      query: {
        filter: { LicenseName: groupName, OrganizationId: guid(organizationId) },
      },
    });

    if (duplicatedGroups?.count) {
      setCustomError(t('ThereIsAlreadyAStructmasterLicenseWithThisName'));
      return;
    }

    setCustomError('');

    const body = {
      name: curateString(item.name),
      organizationId: item.organization.value,
      members: (item.members || []).map((x: any) => x.user.value),
      groupType: GroupType.Structmaster,
      roleExpirationDate: item.expirationDate,
    } as CreateUnsignedStructmasterLicense;

    setIsLoading(true);
    let response = await structmasterLicensesStore.createUnsignedLicense(body);
    setIsLoading(false);
    if (response?.isSuccess) {
      form.resetFields();
      onClose(response);
    } else {
      setCustomError(t('ErrorCreatingLicense'));
    }
  };

  const loadDisabledUsers = async (organizationId: string) => {
    await organizationStore.load(organizationId);
    setCurrentOrganizationMembers((organizationState?.item?.value?.members ?? [])?.map((v) => v.userId) ?? []);

    const users = await structmasterLicensesStore.getUsersAlreadyOnGroupType(organizationId, GroupType.Structmaster);
    if (users) disabledUsersRef.current = users;
  };

  return (
    <NewItemModal
      className="figma-modal"
      title={t('AddNewStructmasterLicense')}
      onNewItem={canCreateUnsigned ? onNewItem : onNewItemUnsigned}
      onClose={onClose}
      onOkButtonIsDisabled={
        newGroupState.isBusy.get() ||
        formUsers.filter((formUser) => !currentOrganizationMembers.includes(formUser?.value)).length + currentOrganizationMembers.length >
          ORGANIZATIONS_MEMBER_LIMIT
      }
      onCancelButtonIsDisabled={newGroupState.isBusy.get()}
      closable={!newGroupState.isBusy.get()}
      maskClosable={!newGroupState.isBusy.get()}
      okText={t('AddLicense')}
      errorMessage={newGroupState.errorMessage}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin spinning={newGroupState.isBusy.get() || organizationState.isBusy.get() || isLoading}>
            {formUsers.filter((formUser) => formUser?.value && !currentOrganizationMembers.includes(formUser?.value)).length +
              currentOrganizationMembers.length >
              ORGANIZATIONS_MEMBER_LIMIT && (
              <Alert
                type="error"
                message={t('NewGroupUserCountErrorParam')
                  .replace('{0}', `${ORGANIZATIONS_MEMBER_LIMIT}`)
                  .replace('{1}', `${currentOrganizationMembers.length}`)
                  .replace(
                    '{2}',
                    `${formUsers.filter((formUser) => formUser?.value && !currentOrganizationMembers.includes(formUser?.value)).length}`
                  )}
              />
            )}
            <Alert type="warning" message={t('StructmasterLicensesOnlyOneActiveUserWarning')} />
            {formUsers.filter((formUser) => !currentOrganizationMembers.includes(formUser?.value) && formUser?.value).length > 0 && (
              <Alert
                type="warning"
                message={
                  <>
                    {t('TheFollowingUsersWillBeAddedToTheOrganization')}
                    <br />
                    <br />
                    {formUsers
                      .filter((formUser) => !currentOrganizationMembers.includes(formUser?.value) && formUser?.label)
                      .map((v) => (
                        <>
                          - {v?.label}
                          <br />
                        </>
                      ))}
                  </>
                }
              />
            )}{' '}
            <Form form={form} layout="vertical" autoComplete="off">
              <Tabs defaultActiveKey="1">
                <Tabs.TabPane className="ant-tabs-tabpane-without-border" tab={t('LicenseInfo')} key="1">
                  <Form.Item
                    name={nameof<CreateGroupItem>('name')}
                    rules={[
                      { required: true, message: t('PleaseEnterName') },
                      () => ({
                        validator(_, value: string) {
                          if (value && value.trim().length === 0) return Promise.reject(t('PleaseEnterName'));
                          return Promise.resolve();
                        },
                      }),
                      () => ({
                        validator: (ruleObj: RuleObject, value: string) => {
                          const maxLenght = 128;
                          if (value === undefined) return Promise.resolve();
                          if (value.length > maxLenght) {
                            return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Name')).replace('{1}', `${maxLenght}`));
                          } else return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input autoComplete="off" allowClear placeholder={t('LicenseName')} />
                  </Form.Item>
                  <Form.Item name="organization" rules={[{ required: true, message: t('PleaseSelect') }]}>
                    <OrganizationsSelect onChange={({ value }) => loadDisabledUsers(value)} />
                  </Form.Item>

                  <Row>
                    <Col span={12}>
                      <RoleAssignmentAttributesComponent showLabel={false} style={{ width: '100%' }} onChange={() => {}} />
                    </Col>
                  </Row>
                </Tabs.TabPane>
                <Tabs.TabPane forceRender className="ant-tabs-tabpane-without-border" tab={t('Users')} key="2">
                  <Form.List name="members">
                    {(fields, { add, remove }, { errors }) => (
                      <>
                        <Form.ErrorList errors={errors} />
                        <div id="new-organization-users-container" style={{ maxHeight: '40vh', overflowX: 'auto' }}>
                          {fields.map(({ key, name: fieldIndx, fieldKey, ...restField }) => (
                            <Row key={fieldKey}>
                              <Col span={20}>
                                <Form.Item
                                  {...restField}
                                  style={{ marginRight: 10, height: '100%' }}
                                  name={[fieldIndx, 'user']}
                                  //fieldKey={[fieldKey, 'user']}
                                  rules={[
                                    () => ({
                                      required: true,
                                      validator(_, value) {
                                        if (value?.value == null) return Promise.reject(t('SelectOrInviteWarning'));
                                        if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
                                        return Promise.resolve();
                                      },
                                    }),
                                    () => ({
                                      validator(_, value) {
                                        if (value?.value == null) return Promise.resolve();
                                        if (disabledUsersRef.current.includes(value.value))
                                          return Promise.reject(t('UserAlreadyIsStructmasterUserError'));
                                        return Promise.resolve();
                                      },
                                    }),
                                    (formInstance) => ({
                                      message: t('DuplicatedUserWarning'),
                                      validator(_, formItem) {
                                        const items: { user: { label: string; value: string } }[] = formInstance.getFieldValue('members');

                                        if (formItem === undefined) return Promise.resolve();

                                        if (items.filter((item) => item && item.user.value === formItem.value).length > 1) {
                                          return Promise.reject();
                                        } else {
                                          return Promise.resolve();
                                        }
                                      },
                                    }),
                                  ]}
                                >
                                  <UserAutocomplete
                                    onChange={(user) => {
                                      setFormUsers(
                                        form.getFieldsValue().members.map((m: { user: { label: string; value: string } | undefined }) => m?.user)
                                      );
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={2} style={{ textAlign: 'center' }}>
                                <MinusCircleOutlined
                                  style={{ fontSize: 20 }}
                                  onClick={() => {
                                    setGroupUserCount(groupUserCount - 1);
                                    remove(fieldIndx);
                                    setFormUsers(
                                      form.getFieldsValue().members.map((m: { user: { label: string; value: string } | undefined }) => m?.user)
                                    );
                                  }}
                                />
                              </Col>
                            </Row>
                          ))}
                        </div>
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => {
                              add();
                              setGroupUserCount(groupUserCount + 1);
                            }}
                            block
                            icon={<PlusOutlined />}
                          >
                            {t('AddUser')}
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Tabs.TabPane>
              </Tabs>
            </Form>
          </Spin>
        </>
      }
    />
  );
};

export default StructmasterLicenseGroupAssignment;
